import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  Alert,
  AlertVariants,
  Divider,
  MixpanelEventSource,
  MixpanelSourceProvider,
  POSITION,
  PositionUpdateSourceEnum,
  ProductTypeEnum,
  UnderlyingPriceContext,
  useMarketAccountsContext,
  useObservable,
  useSecuritiesContext,
  useSubscription,
  type IPositionUpdate,
} from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { shareReplay } from 'rxjs';
import { OptionFilter } from './Filter/OptionFilter';
import { updateMeta } from './Filter/filterSlice';
import { selectLoading, selectOptionDataError, selectOptionFilter, selectUnderlyingAmount } from './Filter/selectors';
import { OptionBalances } from './OptionBalances';
import { OptionsBlotter } from './OptionsBlotter';
import type { OptionsProps } from './hooks/useOptionsTab';
import { useOptionsTab } from './hooks/useOptionsTab';
import { OptionExpiryContainer, OptionWrapper } from './styles';
import { DEFAULT_OPTION_COLUMNS_IS_VISIBLE } from './types';

export function Options(props: OptionsProps) {
  const dispatch = useAppStateDispatch();
  const { expirationByMarketByCurrencyIdentity, optionSecurityBySymbol } = useSecuritiesContext();
  const { marketAccountsByMarket } = useMarketAccountsContext();

  // Keep redux meta of options up to date.
  useEffect(() => {
    dispatch(updateMeta({ expirationByMarketByCurrencyIdentity, optionSecurityBySymbol, marketAccountsByMarket }));
  }, [dispatch, expirationByMarketByCurrencyIdentity, optionSecurityBySymbol, marketAccountsByMarket]);

  useOptionsTab(props);
  const isLoading = useAppStateSelector(state => selectLoading(state, props.id));

  const optionFilter = useAppStateSelector(state => selectOptionFilter(state, props.id));
  const { data } = useSubscription<IPositionUpdate>({
    name: POSITION,
    tag: 'OPTION/POSITIONS',
    MarketAccounts: optionFilter?.marketAccountName ? [optionFilter.marketAccountName] : undefined,
    ShowZeroBalances: false,
    AssetTypes: [ProductTypeEnum.Option],
    Source: PositionUpdateSourceEnum.Gateway,
  });

  const positionsSubscription = useObservable(() => data.pipe(shareReplay({ refCount: true })), [data]);
  const underlyingAmountRecord = useAppStateSelector(state => selectUnderlyingAmount(state, props.id));

  const underlyingAmountContextValue = useMemo(() => {
    return {
      underlyingAmountRecord,
      currency: optionFilter?.currency,
      market: optionFilter?.market,
    };
  }, [underlyingAmountRecord, optionFilter?.currency, optionFilter?.market]);

  const optionError = useAppStateSelector(state => selectOptionDataError(state, props.id));

  if (isLoading || optionFilter == null) {
    return null;
  }

  return (
    <OptionWrapper background="colors.gray.main">
      {/* Remount filter when the tab changes, filter has internal state that needs to be wiped */}
      <OptionFilter panelID={props.id} key={`filter-${props.id}`} />
      <Divider orientation="horizontal" color="gray.010" />
      <OptionBalances panelID={props.id} />

      {optionError?.marketData && (
        <>
          <Alert dismissable={false} variant={AlertVariants.Warning} mx="spacing8">
            {optionError.marketData}
          </Alert>
        </>
      )}
      <Divider mt={optionError?.marketData ? 'spacing4' : undefined} orientation="horizontal" color="gray.010" />

      <OptionExpiryContainer w="100%" h="100%">
        <UnderlyingPriceContext.Provider value={underlyingAmountContextValue}>
          <MixpanelSourceProvider value={MixpanelEventSource.OptionsChain}>
            {/* Remount blotter when the tab changes */}
            <OptionsBlotter
              key={`blotter-${props.id}`}
              panelID={props.id}
              initialColumnDefsVisibility={props.columnDefsVisibility || DEFAULT_OPTION_COLUMNS_IS_VISIBLE}
              positionsSubscription={positionsSubscription}
            />
          </MixpanelSourceProvider>
        </UnderlyingPriceContext.Provider>
      </OptionExpiryContainer>
    </OptionWrapper>
  );
}
